import { BookOpen, Lightbulb, Languages, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type { GuruExplanation, HawkReview, WordBreakdown, GrammarPoint } from "@/types/phrase"

interface GuruExplanationCardProps {
  explanation: GuruExplanation
  hawkReview?: HawkReview
  className?: string
}

const verificationConfig = {
  accurate: {
    label: "Verified by Hawk",
    icon: ShieldCheck,
    badgeClass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
    borderClass: "border-emerald-200/60 dark:border-emerald-900/40",
  },
  partially_accurate: {
    label: "Partially verified",
    icon: ShieldAlert,
    badgeClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
    borderClass: "border-amber-200/60 dark:border-amber-900/40",
  },
  inaccurate: {
    label: "Accuracy concerns",
    icon: ShieldX,
    badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
    borderClass: "border-red-200/60 dark:border-red-900/40",
  },
} as const

/**
 * Renders guru explanation content on public phrase pages.
 * Designed for SEO (semantic HTML) and visual distinction.
 * Static card layout - no accordion/collapse for crawler visibility.
 */
export function GuruExplanationCard({ explanation, hawkReview, className }: GuruExplanationCardProps) {
  const { explanation: data } = explanation

  if (!data) return null

  const { context, breakdown, grammarPoints } = data
  const hasContent = context || breakdown?.length || grammarPoints?.length

  if (!hasContent) return null

  const verification = hawkReview?.accuracyLevel
    ? verificationConfig[hawkReview.accuracyLevel]
    : null

  return (
    <Card className={cn(
      "overflow-hidden bg-gradient-to-br from-amber-50/80 via-white to-orange-50/40",
      "dark:from-amber-950/30 dark:via-slate-900 dark:to-orange-950/20",
      verification ? verification.borderClass : "border-amber-200/60 dark:border-amber-900/40",
      className
    )}>
      {/* Header */}
      <div className="border-b border-amber-200/60 bg-amber-50/50 px-6 py-4 dark:border-amber-900/40 dark:bg-amber-950/20">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-3 text-lg font-semibold text-amber-900 dark:text-amber-100">
            <div className="flex size-10 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/50">
              <BookOpen className="size-5 text-amber-700 dark:text-amber-300" />
            </div>
            Language Breakdown
          </h2>
          {verification && (
            <span className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
              verification.badgeClass
            )}>
              <verification.icon className="size-3.5" />
              {verification.label}
            </span>
          )}
        </div>
      </div>

      <CardContent className="space-y-8 p-6 sm:p-8">
        {/* Context Section */}
        {context && (
          <section aria-labelledby="context-heading">
            <h3
              id="context-heading"
              className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-amber-800/70 dark:text-amber-200/70"
            >
              <Lightbulb className="size-4" />
              Cultural Context
            </h3>
            <p className="text-base leading-relaxed text-slate-700 dark:text-slate-300">
              {context}
            </p>
          </section>
        )}

        {/* Word Breakdown Table */}
        {breakdown && breakdown.length > 0 && (
          <section aria-labelledby="breakdown-heading">
            <h3
              id="breakdown-heading"
              className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-amber-800/70 dark:text-amber-200/70"
            >
              <Languages className="size-4" />
              Word-by-Word Analysis
            </h3>

            {/* Table header - desktop only */}
            <div className="mb-2 hidden text-[10px] font-medium uppercase tracking-widest text-slate-500 sm:grid sm:grid-cols-[minmax(80px,1fr)_minmax(100px,1.2fr)_minmax(120px,2fr)] sm:gap-4 sm:px-4 dark:text-slate-400">
              <span>Thai</span>
              <span>Pronunciation</span>
              <span>Meaning</span>
            </div>

            {/* Semantic definition list for SEO */}
            <dl className="divide-y divide-amber-200/50 overflow-hidden rounded-xl border border-amber-200/60 bg-white dark:divide-amber-800/30 dark:border-amber-800/40 dark:bg-slate-900/50">
              {breakdown.map((item: WordBreakdown, idx: number) => (
                <div
                  key={idx}
                  className={cn(
                    "grid grid-cols-[1fr_1fr] gap-4 p-4 sm:grid-cols-[minmax(80px,1fr)_minmax(100px,1.2fr)_minmax(120px,2fr)]",
                    idx % 2 === 0
                      ? "bg-amber-50/30 dark:bg-amber-950/10"
                      : "bg-white dark:bg-slate-900/50"
                  )}
                >
                  {/* Thai Word */}
                  <div className="flex flex-col">
                    <dt className="text-[10px] font-medium uppercase tracking-widest text-slate-400 dark:text-slate-500 sm:hidden">
                      Thai
                    </dt>
                    <dd className="text-xl font-medium text-slate-900 dark:text-white">
                      {item.word}
                    </dd>
                  </div>

                  {/* Romanization */}
                  <div className="flex flex-col">
                    <dt className="text-[10px] font-medium uppercase tracking-widest text-slate-400 dark:text-slate-500 sm:hidden">
                      Pronunciation
                    </dt>
                    <dd className="font-mono text-base italic text-amber-700 dark:text-amber-400">
                      {item.romanization}
                    </dd>
                  </div>

                  {/* Meaning + Part of Speech */}
                  <div className="col-span-2 flex flex-col sm:col-span-1">
                    <dt className="text-[10px] font-medium uppercase tracking-widest text-slate-400 dark:text-slate-500 sm:hidden">
                      Meaning
                    </dt>
                    <dd className="text-base text-slate-700 dark:text-slate-300">
                      {item.meaning}
                      {item.partOfSpeech && (
                        <span className="ml-2 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                          {item.partOfSpeech}
                        </span>
                      )}
                    </dd>
                  </div>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* Grammar Points */}
        {grammarPoints && grammarPoints.length > 0 && (
          <section aria-labelledby="grammar-heading">
            <h3
              id="grammar-heading"
              className="mb-4 text-sm font-semibold uppercase tracking-wider text-amber-800/70 dark:text-amber-200/70"
            >
              Grammar Notes
            </h3>
            <ul className="space-y-3">
              {grammarPoints.map((point: GrammarPoint, idx: number) => (
                <li
                  key={idx}
                  className="flex gap-3 rounded-lg border border-amber-200/40 bg-white/60 p-4 dark:border-amber-800/30 dark:bg-slate-900/40"
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                    {idx + 1}
                  </span>
                  <div>
                    <strong className="text-sm font-semibold text-slate-900 dark:text-white">
                      {point.rule}
                    </strong>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                      {point.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  )
}
